/**
 * Triggers a file input click programmatically.
 * We use a hidden input + button pattern instead of styling the input directly
 * because file inputs have limited styling options across browsers.
 * @param fileInputId - ID of the file input element to trigger
 */
export const triggerFileInputClick = (fileInputId: string): void => {
  document.getElementById(fileInputId)?.click();
};

/**
 * Validates file signature based on file extension and content
 * @param base64 - Base64 encoded file content
 * @param filename - File name with extension
 * @returns true if file signature matches the extension
 */
export const validateFileSignature = (
  base64: string,
  filename: string,
): boolean => {
  try {
    const extension = filename.toLowerCase().split(".").pop();

    // Remove data URL prefix if present
    const cleanBase64 = base64.includes(",") ? base64.split(",")[1] : base64;

    // Decode base64 to binary
    const binary = atob(cleanBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    switch (extension) {
      case "pdf":
        // %PDF signature
        return (
          bytes[0] === 0x25 &&
          bytes[1] === 0x50 &&
          bytes[2] === 0x44 &&
          bytes[3] === 0x46
        );
      case "jpg":
      case "jpeg":
        // JPEG signature: FF D8
        return bytes[0] === 0xff && bytes[1] === 0xd8;
      case "png":
        // PNG signature: 89 50 4E 47 0D 0A 1A 0A
        return (
          bytes[0] === 0x89 &&
          bytes[1] === 0x50 &&
          bytes[2] === 0x4e &&
          bytes[3] === 0x47 &&
          bytes[4] === 0x0d &&
          bytes[5] === 0x0a &&
          bytes[6] === 0x1a &&
          bytes[7] === 0x0a
        );
      default:
        return false;
    }
  } catch {
    return false;
  }
};
